import { Plug, Lightbulb, Droplet, GaugeCircle, Radar, Thermometer, DoorOpen, Package, Rss, Waypoints } from "lucide-react";

/**
 * Store product catalog — single source of truth for both Store index and detail pages.
 * All products are pre-release (status: "Coming Soon"). No prices, no reviews, no stock.
 */

export const CATEGORIES = [
  { id: "all", label: "All devices" },
  { id: "energy", label: "Energy Monitoring" },
  { id: "water", label: "Water Protection" },
  { id: "comfort", label: "Comfort & Automation" },
  { id: "kits", label: "NOA Kits" },
];

export const PRODUCTS = [
  {
    slug: "smart-plug",
    name: "Smart Plug",
    category: "energy",
    categoryLabel: "Energy Monitoring",
    icon: Plug,
    tint: "from-forest/10 to-moss/10",
    short: "Meter and switch any appliance from the NOA app.",
    compatibility: "Wi-Fi 2.4 GHz · Works with NOA app",
    overview:
      "The NOA Smart Plug turns any plugged-in appliance into a monitored, schedulable device. Slot it between the outlet and the appliance to see live wattage, cumulative energy use, and to switch the appliance on or off remotely.",
    problem:
      "'Vampire' loads and forgotten devices quietly account for a meaningful share of a household's electricity bill. The Smart Plug surfaces exactly which appliance is drawing power and lets NOA power it down when it isn't needed.",
    installation_area: "Living room, kitchen, home office, entertainment center — any standard wall outlet.",
    app_connection:
      "Pairs to the NOA app over Wi-Fi via the in-app onboarding flow. Once linked, the plug appears in the room map and can be added to automations.",
    hub_requirement: "No dedicated hub required. Requires a 2.4 GHz Wi-Fi network within range.",
    alerts_automations: [
      "Live power draw notifications when a device exceeds a threshold you set",
      "Schedule-based on/off (e.g., turn off entertainment stack after 11pm)",
      "Presence-based automation with a NOA Motion Sensor (turn off when the room is empty)",
    ],
    specs: [
      ["Connectivity", "Wi-Fi 2.4 GHz (802.11 b/g/n)"],
      ["Input", "100–240 V AC, 50/60 Hz"],
      ["Max load", "Rated per regional plug variant (see packaging)"],
      ["Metering", "Real-time active power and cumulative energy"],
    ],
    package_contents: ["1× NOA Smart Plug", "Quick-start card", "Recycled-paper packaging"],
    warranty: "24-month limited manufacturer warranty against defects. Register within the NOA app after activation.",
    installation: "Plug-and-play — no wiring or electrician required. Pair from the NOA app in under two minutes.",
  },
  {
    slug: "smart-light-switch",
    name: "Smart Light Switch",
    category: "energy",
    categoryLabel: "Energy Monitoring",
    icon: Lightbulb,
    tint: "from-forest/10 to-terracotta/10",
    short: "Retrofit existing switches for scheduled and motion-triggered lighting.",
    compatibility: "Neutral wire required · Works with NOA app",
    overview:
      "A retrofit wall switch that replaces an existing on/off switch. Adds NOA app control, schedules and occupancy-linked lighting to any circuit — no smart bulbs required.",
    problem:
      "Lighting left on in unoccupied rooms is one of the most common household energy drains. The Smart Light Switch pairs with NOA occupancy signals to switch lighting off automatically.",
    installation_area: "Any single-gang wall switch position with a neutral wire (hallways, bedrooms, bathrooms, common areas).",
    app_connection:
      "Pairs to the NOA app via Wi-Fi during setup. Appears in the app as a controllable device within its assigned room.",
    hub_requirement: "No dedicated hub required. Requires 2.4 GHz Wi-Fi coverage.",
    alerts_automations: [
      "Scheduled on/off by time-of-day",
      "Auto-off after configurable idle period",
      "Trigger from motion, door or ambient light events",
    ],
    specs: [
      ["Connectivity", "Wi-Fi 2.4 GHz"],
      ["Wiring", "Line, load and neutral (neutral wire required)"],
      ["Voltage", "100–240 V AC"],
      ["Form factor", "Single-gang wall plate"],
    ],
    package_contents: ["1× NOA Smart Light Switch", "Wire nuts", "Mounting screws", "Installation guide"],
    warranty: "24-month limited manufacturer warranty. Professional installation recommended where required by local code.",
    installation: "Requires switching off the circuit at the breaker and rewiring the switch position. A licensed electrician is recommended if you are unsure.",
  },
  {
    slug: "leak-sensor",
    name: "Leak Sensor",
    category: "water",
    categoryLabel: "Water Protection",
    icon: Droplet,
    tint: "from-sky-400/10 to-forest/10",
    short: "Detect leaks under sinks, behind toilets and beside appliances before they spread.",
    compatibility: "Battery-powered · Works with NOA app",
    overview:
      "A small wireless leak detector placed on the floor near common failure points. When water is detected, NOA sends an immediate push notification and can trigger a connected Smart Water Valve to shut off the main line.",
    problem:
      "Slow, hidden leaks damage floors, cabinetry and neighboring units — often unnoticed for days. The Leak Sensor catches leaks the moment they start and enables automatic shut-off when paired with the NOA Smart Water Valve.",
    installation_area: "Under kitchen and bathroom sinks, behind toilets, next to washing machines and water heaters.",
    app_connection: "Pairs to the NOA app during setup. Shows a live 'dry / wet / low-battery' status per sensor.",
    hub_requirement: "No dedicated hub required for the base experience. Automation with the Smart Water Valve is handled inside the NOA app.",
    alerts_automations: [
      "Instant push notification the moment moisture is detected",
      "Automatic shut-off trigger when paired with the NOA Smart Water Valve",
      "Low-battery reminder in the NOA app",
    ],
    specs: [
      ["Connectivity", "Wireless to the NOA app"],
      ["Power", "Replaceable coin-cell battery (typical life 12+ months)"],
      ["Ingress rating", "Splash-resistant floor contacts"],
      ["Alerting", "Local buzzer + app push notification"],
    ],
    package_contents: ["1× NOA Leak Sensor", "Pre-installed battery", "Quick-start card"],
    warranty: "12-month limited manufacturer warranty against defects.",
    installation: "Place on a flat, dry surface where water would first accumulate. No wiring or hub required.",
  },
  {
    slug: "smart-water-valve",
    name: "Smart Water Valve",
    category: "water",
    categoryLabel: "Water Protection",
    icon: GaugeCircle,
    tint: "from-sky-500/10 to-moss/10",
    short: "Automatically shut off the main water line when a leak is detected.",
    compatibility: "Standard main-line fitting · Works with NOA app",
    overview:
      "A motorised valve that fits onto the main water supply. Pairs with NOA Leak Sensors to shut off water automatically when a leak is detected, and can be manually toggled from the NOA app when leaving the house.",
    problem:
      "Detection alone doesn't stop water damage — someone still has to act. The Smart Water Valve closes the main line automatically the moment a paired Leak Sensor senses moisture.",
    installation_area: "On the main water supply line where accessible. Professional plumber installation recommended.",
    app_connection: "Pairs to the NOA app during setup. Manual open / close controls are available in-app, with a physical override on the unit.",
    hub_requirement: "No dedicated hub required. Uses your home Wi-Fi.",
    alerts_automations: [
      "Auto-close on paired leak sensor trigger",
      "'Away mode' — auto-close the main line when the last device in the home goes idle",
      "Scheduled close (e.g., overnight or during holidays)",
    ],
    specs: [
      ["Connectivity", "Wi-Fi 2.4 GHz"],
      ["Actuation", "Motorised ball valve"],
      ["Fitting", "Standard threaded connection (region-specific variant)"],
      ["Manual override", "Physical lever on unit"],
    ],
    package_contents: ["1× NOA Smart Water Valve", "Mounting hardware", "Installation guide"],
    warranty: "24-month limited manufacturer warranty. Professional plumbing installation is required to maintain warranty coverage.",
    installation: "Requires installation by a qualified plumber. Local plumbing regulations apply.",
  },
  {
    slug: "motion-sensor",
    name: "Motion Sensor",
    category: "comfort",
    categoryLabel: "Comfort & Automation",
    icon: Radar,
    tint: "from-forest/10 to-terracotta/10",
    short: "Trigger lighting, climate and 'goodbye' routines based on room occupancy.",
    compatibility: "Battery-powered · Works with NOA app",
    overview:
      "A discreet occupancy sensor that helps NOA understand which rooms are in use, in real time. Feeds the room map, powers presence-based automations, and keeps rooms from wasting energy when empty.",
    problem:
      "Lights, AC and entertainment often stay on in unoccupied rooms. NOA uses motion signals to auto-off the right devices — and never touches them while you're using them.",
    installation_area: "Corner of any room, at ~2m height. Best coverage with a clear line of sight to the room.",
    app_connection: "Pairs to the NOA app. Shows occupancy state per room on the dashboard.",
    hub_requirement: "No dedicated hub required.",
    alerts_automations: [
      "Auto-off lighting after a configurable idle period",
      "Auto-adjust climate when a room is empty",
      "'Goodbye' routine — power down non-essential devices when the last motion event ages out",
    ],
    specs: [
      ["Connectivity", "Wireless to the NOA app"],
      ["Power", "Replaceable coin-cell battery (typical life 18+ months)"],
      ["Detection", "Passive infrared with configurable sensitivity"],
      ["Mounting", "Adhesive pad or screw"],
    ],
    package_contents: ["1× NOA Motion Sensor", "Mounting pad and screw", "Pre-installed battery"],
    warranty: "12-month limited manufacturer warranty.",
    installation: "Peel-and-stick — no wiring. Pair from the NOA app and place at the recommended height.",
  },
  {
    slug: "temperature-humidity-sensor",
    name: "Temperature and Humidity Sensor",
    category: "comfort",
    categoryLabel: "Comfort & Automation",
    icon: Thermometer,
    tint: "from-terracotta/10 to-moss/10",
    short: "Give NOA per-room climate awareness for smarter heating and cooling.",
    compatibility: "Battery-powered · Works with NOA app",
    overview:
      "A compact sensor that reports temperature and relative humidity per room. NOA uses per-room readings to tune schedules and to warn about damp or overheating zones.",
    problem:
      "Homes are usually thermostatted from a single point — but real comfort and efficiency depend on how each room is actually behaving. Per-room readings let NOA make finer, cheaper decisions.",
    installation_area: "Any interior wall, away from direct sunlight and vents.",
    app_connection: "Pairs to the NOA app during setup. Live values shown on the dashboard and used by climate automations.",
    hub_requirement: "No dedicated hub required.",
    alerts_automations: [
      "High-humidity alert (mould-risk threshold)",
      "Room-level comfort schedule adjustments",
      "Overheat warning when a room exceeds a set ceiling",
    ],
    specs: [
      ["Connectivity", "Wireless to the NOA app"],
      ["Power", "Replaceable coin-cell battery"],
      ["Ranges", "Temperature and relative humidity (see packaging for calibrated range)"],
      ["Mounting", "Adhesive pad"],
    ],
    package_contents: ["1× NOA Temperature and Humidity Sensor", "Mounting pad", "Pre-installed battery"],
    warranty: "12-month limited manufacturer warranty.",
    installation: "Peel-and-stick. No tools required. Pair from the NOA app.",
  },
  {
    slug: "door-window-sensor",
    name: "Door and Window Sensor",
    category: "comfort",
    categoryLabel: "Comfort & Automation",
    icon: DoorOpen,
    tint: "from-forest/10 to-sky-500/10",
    short: "Know when a door or window is left open — and let NOA respond.",
    compatibility: "Battery-powered · Works with NOA app",
    overview:
      "A two-piece magnetic contact sensor for any door or window. NOA uses open/close state to pause climate control on open windows, trigger 'away' routines, and confirm doors are shut before shutdown.",
    problem:
      "Heating and cooling an open room is one of the fastest ways to waste energy. Open-state awareness lets NOA pause conditioning until the door or window is closed again.",
    installation_area: "Any hinged door or sliding window frame.",
    app_connection: "Pairs to the NOA app. Live open/closed state shown per sensor.",
    hub_requirement: "No dedicated hub required.",
    alerts_automations: [
      "Notification if a door or window is left open beyond a threshold",
      "Auto-pause climate control while any window is open",
      "'Away' confirmation — flag any open opening when the last person leaves",
    ],
    specs: [
      ["Connectivity", "Wireless to the NOA app"],
      ["Power", "Replaceable coin-cell battery"],
      ["Detection", "Magnetic contact"],
      ["Mounting", "Adhesive pad"],
    ],
    package_contents: ["1× NOA Door and Window Sensor (two-piece)", "Mounting pads", "Pre-installed battery"],
    warranty: "12-month limited manufacturer warranty.",
    installation: "Peel-and-stick on the frame and the moving edge. Align the two halves per the guide.",
  },
  {
    slug: "smart-ir-remote",
    name: "Smart IR Remote Control",
    category: "comfort",
    categoryLabel: "Comfort & Automation",
    icon: Rss,
    tint: "from-terracotta/10 to-forest/10",
    short: "Bring legacy AC, TV and audio gear into NOA — no replacements needed.",
    compatibility: "Wi-Fi 2.4 GHz · IR line-of-sight · Works with NOA app",
    overview:
      "A wireless infrared blaster that mimics your existing remotes, giving NOA the ability to control legacy devices — air conditioners, TVs, fans, set-top boxes — that don't otherwise have smart integrations.",
    problem:
      "Most homes already own a working AC and TV that predate smart-home tech. Replacing them is wasteful. The Smart IR Remote lets NOA control them as-is, so occupancy and schedule automations extend to gear you already own.",
    installation_area: "Central location with clear line of sight to the IR-controlled devices — a living-room shelf pointed at the AC and TV works well.",
    app_connection: "Pairs to the NOA app during setup. Record existing remote codes in-app or select a device from the built-in code library.",
    hub_requirement: "No dedicated hub required. Requires 2.4 GHz Wi-Fi.",
    alerts_automations: [
      "Auto-off AC or TV when the room is empty (paired with NOA Motion Sensor)",
      "Scheduled TV/audio power-down at bedtime",
      "Sync AC set-point to a per-room Temperature & Humidity Sensor",
    ],
    specs: [
      ["Connectivity", "Wi-Fi 2.4 GHz"],
      ["IR emitter", "360° coverage, ~10 m line-of-sight"],
      ["Power", "USB-C (adapter sold with regional variants)"],
      ["Code library", "Pre-loaded profiles for common AC, TV and audio brands"],
    ],
    package_contents: ["1× NOA Smart IR Remote", "USB-C cable", "Adhesive shelf pad", "Quick-start card"],
    warranty: "24-month limited manufacturer warranty against defects.",
    installation: "Plug into any USB-C power source, place with a clear line of sight to the target devices, and pair from the NOA app.",
  },
  {
    slug: "smart-hub-gateway",
    name: "Smart Hub Gateway (Zigbee Mesh)",
    category: "comfort",
    categoryLabel: "Comfort & Automation",
    icon: Waypoints,
    tint: "from-forest/15 to-sky-500/10",
    short: "The Zigbee mesh backbone for larger homes and property-wide NOA deployments.",
    compatibility: "Zigbee 3.0 mesh + Wi-Fi / Ethernet backhaul · Works with NOA app",
    overview:
      "A dedicated Zigbee 3.0 hub with mesh repeating. Extends NOA connectivity beyond Wi-Fi range and supports larger fleets of battery sensors — ideal for multi-floor homes, larger properties and portfolio deployments.",
    problem:
      "Bigger homes and property portfolios can strain Wi-Fi when many battery sensors are online. A Zigbee mesh scales cleanly, preserves sensor battery life, and keeps automations running even during brief internet outages.",
    installation_area: "A central location in the property with wired power, plus Ethernet or Wi-Fi backhaul.",
    app_connection: "Provisioned once through the NOA app during setup. After that, all Zigbee-based NOA devices pair through the hub.",
    hub_requirement: "This is the hub. Required for Zigbee-based device fleets and property-scale deployments; optional for single-home Wi-Fi-only setups.",
    alerts_automations: [
      "Whole-home Zigbee mesh routing for reliable coverage",
      "Scales to larger device counts than Wi-Fi-only deployments",
      "Locally cached rules keep core automations running during brief internet outages",
    ],
    specs: [
      ["Protocol", "Zigbee 3.0 (mesh)"],
      ["Backhaul", "Wi-Fi 2.4 / 5 GHz + Ethernet fallback"],
      ["Device capacity", "Property-scale; regional variants may differ"],
      ["Form factor", "Low-profile enclosure with wall or shelf mount"],
    ],
    package_contents: ["1× NOA Smart Hub Gateway", "Power adapter", "Ethernet cable", "Mounting hardware", "Quick-start card"],
    warranty: "24-month limited manufacturer warranty against defects.",
    installation: "Plug in, connect via Ethernet or pair with your Wi-Fi, then follow the NOA app onboarding to enroll Zigbee devices.",
  },
  {
    slug: "noa-home-starter-kit",
    name: "NOA Home Starter Kit",
    category: "kits",
    categoryLabel: "NOA Kits",
    icon: Package,
    tint: "from-forest/15 to-terracotta/15",
    short: "The smallest bundle that gets a full NOA experience running in a small home.",
    compatibility: "Bundle · Works with NOA app",
    overview:
      "A curated set of NOA devices selected to cover the highest-impact use cases in a typical small home: energy metering, water safety, and per-room comfort awareness. Designed so a new customer can activate NOA meaningfully on day one.",
    problem:
      "Buying devices one-by-one leaves gaps. The Starter Kit gives NOA enough signal — energy, water and motion — to deliver useful savings recommendations from the first week.",
    installation_area: "Small to mid-size homes and apartments.",
    app_connection: "All included devices pair through the NOA onboarding flow. A single guided setup covers the full kit.",
    hub_requirement: "No dedicated hub required. Requires 2.4 GHz Wi-Fi.",
    alerts_automations: [
      "Room-by-room energy monitoring",
      "Leak detection with app alerting",
      "Occupancy-linked lighting and climate routines",
    ],
    specs: [
      ["Contents", "Selected NOA devices — see 'Package contents' below"],
      ["Coverage", "Sized for small home or apartment"],
      ["Connectivity", "Wi-Fi 2.4 GHz for hub-less pairing"],
    ],
    package_contents: [
      "2× NOA Smart Plug",
      "1× NOA Motion Sensor",
      "1× NOA Leak Sensor",
      "1× NOA Temperature and Humidity Sensor",
      "Getting-started guide",
    ],
    warranty: "Each device inherits its individual manufacturer warranty. See per-product pages for details.",
    installation: "All devices are plug-and-play or peel-and-stick. Guided setup inside the NOA app.",
  },
];

export const CATEGORY_LOOKUP = Object.fromEntries(CATEGORIES.map((c) => [c.id, c.label]));

export function findProduct(slug) {
  return PRODUCTS.find((p) => p.slug === slug);
}
